import { StateCreator } from 'zustand';
import type { AppState } from '../../store/app-state';
import { ScheduleEvent, RehearsalScene, Personnel } from '../../types';
import { createLastModifiedBy } from '../../utils/lastModifiedBy';
import {
    saveScheduleEvent,
    deleteScheduleEvent as deleteScheduleEventDocument,
    saveRehearsalScene,
} from '@/features/sync/schedule-api';
import type { ProjectScope } from '@/features/sync/project-scope';
import {
    enqueueStructuredEntityWrite,
    type StructuredEntityWriteRegistration,
} from '@/features/sync/structured-entity-write';
import {
    markPendingScheduleDelete,
    markPendingScheduleWrite,
} from '@/features/sync/schedule-pending-writes';
import { logger } from '@/utils/logger';
import { createFirestoreDeleteFieldSentinel } from '@/utils/firestore-sanitizer';
import { logBetaAnalyticsEvent } from '@/utils/beta-analytics';
import { showToast } from '@/features/layout/notifications';
import { getEffectiveUserId } from '@/features/auth/current-user-identity';

export interface SchedulerSlice {
    rehearsalSchedule: ScheduleEvent[];
    rehearsalScenes: RehearsalScene[];
    schedulerActions: {
        // Schedule Events
        addScheduleEvent: (event: Partial<ScheduleEvent>) => string;
        updateScheduleEvent: (id: string, updates: Partial<ScheduleEvent>) => void;
        deleteScheduleEvent: (id: string) => void;
        
        // Rehearsal Scenes
        addRehearsalScene: (scene: Partial<RehearsalScene>) => string;
        updateRehearsalScene: (id: string, updates: Partial<RehearsalScene>) => void;
        deleteRehearsalScene: (id: string) => void;
        duplicateRehearsalScene: (id: string) => void;
        
        // Conflicts
        updateActorConflicts: (actorId: string, conflicts: string[]) => void;
        
        // Project scoping
        clearScheduler: () => void;
    };
}

const SCHEDULER_PRESERVATION_RETRY_DELAYS_MS = [1000, 5000];
const EXPLICITLY_DELETABLE_SCHEDULE_EVENT_FIELDS = ['callBasis'] as const;

const PERSONNEL_CONFLICT_WRITE_REGISTRATION: StructuredEntityWriteRegistration = {
    feature: 'people_casting',
    capability: 'EDIT_PERSONNEL_OPERATIONAL',
    conflictPrefix: 'personnelSensitive',
    allowedKinds: ['patchEntity'],
    authErrorMessage: 'Authentication required to preserve personnel conflict project writes',
};

function persistSchedulerChange(label: string, action: () => Promise<void> | void, attempt = 0): void {
    try {
        const result = action();
        void Promise.resolve(result).catch(error => {
            logger.error(`[scheduler] Failed to ${label}:`, error);
            const retryDelayMs = SCHEDULER_PRESERVATION_RETRY_DELAYS_MS[attempt];
            if (retryDelayMs !== undefined) {
                window.setTimeout(() => {
                    persistSchedulerChange(label, action, attempt + 1);
                }, retryDelayMs);
                return;
            }
            showToast('Schedule changes are still local. Keep this tab open while preservation recovers.', 'error');
        });
    } catch (error) {
        logger.error(`[scheduler] Failed to ${label}:`, error);
        const retryDelayMs = SCHEDULER_PRESERVATION_RETRY_DELAYS_MS[attempt];
        if (retryDelayMs !== undefined) {
            window.setTimeout(() => {
                persistSchedulerChange(label, action, attempt + 1);
            }, retryDelayMs);
            return;
        }
        showToast('Schedule changes are still local. Keep this tab open while preservation recovers.', 'error');
    }
}

function getProjectScope(state: AppState & SchedulerSlice): ProjectScope | null {
    if (!state.cloud.enabled || !state.cloud.orgId || !state.cloud.projectId) {
        return null;
    }
    return { orgId: state.cloud.orgId, projectId: state.cloud.projectId };
}

export const createSchedulerSlice: StateCreator<
    AppState & SchedulerSlice,
    [["zustand/immer", never]],
    [],
    SchedulerSlice
> = (set, get) => ({
    rehearsalSchedule: [],
    rehearsalScenes: [],
    schedulerActions: {
        addScheduleEvent: (event) => {
            const id = crypto.randomUUID();
            const currentState = get();
            const lastModifiedBy = createLastModifiedBy(
                currentState.auth.user,
                currentState.cloud.member
            );
            const newEvent = {
                id,
                type: event.type || 'custom',
                targetId: event.targetId ?? null,
                title: event.title || '',
                date: event.date || '',
                startTime: event.startTime || '09:00',
                endTime: event.endTime || '10:00',
                location: event.location,
                // Include deadline-specific fields if present
                ...(event.type === 'deadline' && {
                    status: event.status || 'pending',
                    linkedDepartment: event.linkedDepartment || 'general',
                    assignedTo: event.assignedTo,
                }),
                ...event,
                sequence: 0, // Initial sequence number (RFC 5545)
                updatedAt: Date.now(), // Creation timestamp
                lastModifiedBy
            } as ScheduleEvent;

            set(state => {
                state.rehearsalSchedule.push(newEvent);
            });
            const scope = getProjectScope(get());
            if (scope) {
                markPendingScheduleWrite(newEvent.id);
                persistSchedulerChange('save schedule event', () => saveScheduleEvent(scope, newEvent));
            }
            logBetaAnalyticsEvent('beta_schedule_event_created', {
                event_type: String(event.type || 'custom').replace(/[^a-z0-9]+/gi, '_').slice(0, 64),
                source: 'scheduler',
            });
            return id;
        },
        updateScheduleEvent: (id, updates) => {
            const fieldsToDelete = EXPLICITLY_DELETABLE_SCHEDULE_EVENT_FIELDS.filter(
                field => Object.hasOwn(updates, field) && updates[field] === undefined
            );

            set(state => {
                const event = state.rehearsalSchedule.find(e => e.id === id);
                if (event) {
                    const lastModifiedBy = createLastModifiedBy(
                        state.auth.user,
                        state.cloud.member
                    );

                    // Check if significant fields changed (RFC 5545 SEQUENCE increment)
                    const significantFields = ['date', 'startTime', 'endTime', 'title', 'location'];
                    const shouldIncrementSequence = significantFields.some(
                        field => updates[field as keyof typeof updates] !== undefined &&
                                 updates[field as keyof typeof updates] !== event[field as keyof typeof event]
                    );

                    Object.assign(event, updates, {
                        updatedAt: Date.now(),
                        lastModifiedBy,
                        ...(shouldIncrementSequence && { sequence: (event.sequence ?? 0) + 1 })
                    });
                    for (const field of fieldsToDelete) {
                        delete (event as Record<string, unknown>)[field];
                    }
                }
            });
            const scope = getProjectScope(get());
            const updatedEvent = get().rehearsalSchedule.find(e => e.id === id);
            if (scope && updatedEvent) {
                const eventToPersist = fieldsToDelete.length
                    ? { ...updatedEvent } as ScheduleEvent & Record<string, unknown>
                    : updatedEvent;
                for (const field of fieldsToDelete) {
                    (eventToPersist as unknown as Record<string, unknown>)[field] = createFirestoreDeleteFieldSentinel();
                }
                markPendingScheduleWrite(updatedEvent.id);
                persistSchedulerChange('save schedule event', () => saveScheduleEvent(scope, eventToPersist));
            }
        },
        deleteScheduleEvent: (id) => {
            set(state => {
                state.rehearsalSchedule = state.rehearsalSchedule.filter(e => e.id !== id);
            });
            const scope = getProjectScope(get());
            if (scope) {
                markPendingScheduleDelete(id);
                persistSchedulerChange('delete schedule event', () => deleteScheduleEventDocument(scope, id));
            }
        },

        addRehearsalScene: (scene) => {
            const id = crypto.randomUUID();
            const newScene: RehearsalScene = {
                id,
                name: scene.name || 'New Scene',
                characterIds: [],
                relatedSceneIds: [],
                notes: '',
                ...scene
            };
            set(state => {
                if (!state.rehearsalScenes) state.rehearsalScenes = [];
                state.rehearsalScenes.push(newScene);
            });
            const scope = getProjectScope(get());
            if (scope) {
                persistSchedulerChange('save rehearsal scene', () => saveRehearsalScene(scope, newScene));
            }
            return id;
        },
        updateRehearsalScene: (id, updates) => {
            set(state => {
                const scene = state.rehearsalScenes.find(s => s.id === id);
                if (scene) Object.assign(scene, updates);
            });
            const scope = getProjectScope(get());
            const updatedScene = get().rehearsalScenes.find(s => s.id === id);
            if (scope && updatedScene) {
                persistSchedulerChange('save rehearsal scene', () => saveRehearsalScene(scope, updatedScene));
            }
        },
        deleteRehearsalScene: (id) => {
            set(state => {
                const scene = state.rehearsalScenes.find(s => s.id === id);
                // Soft delete usually, but here we can just support both or follow existing pattern
                // Existing was: scene.deleted = true. Let's stick to that if the UI expects it.
                if (scene) scene.deleted = true;
            });
            const scope = getProjectScope(get());
            const deletedScene = get().rehearsalScenes.find(s => s.id === id);
            if (scope && deletedScene) {
                persistSchedulerChange('save rehearsal scene deletion', () => saveRehearsalScene(scope, deletedScene));
            }
        },
        duplicateRehearsalScene: (id) => {
            let copiedScene: RehearsalScene | null = null;
            set(state => {
                const original = state.rehearsalScenes.find(s => s.id === id);
                if (original) {
                    copiedScene = {
                        ...original,
                        id: crypto.randomUUID(),
                        name: `${original.name} (Copy)`
                    };
                    state.rehearsalScenes.push(copiedScene);
                }
            });
            const scope = getProjectScope(get());
            const sceneToSave = copiedScene;
            if (scope && sceneToSave) {
                persistSchedulerChange('save duplicated rehearsal scene', () => saveRehearsalScene(scope, sceneToSave));
            }
        },

        updateActorConflicts: (actorId, conflicts) => {
            set(state => {
                // Access personnel from PersonnelSlice (merged into global state)
                const actor = state.personnel?.find((c: Personnel) => c.id === actorId);
                if (actor) {
                    actor.conflicts = conflicts;
                }
            });

            const currentState = get();
            const scope = getProjectScope(currentState);
            const actor = currentState.personnel?.find((person: Personnel) => person.id === actorId);
            if (scope && actor) {
                const writeActorId = getEffectiveUserId();
                persistSchedulerChange('save actor conflicts', () => enqueueStructuredEntityWrite({
                    scope,
                    actorId: writeActorId,
                    registration: PERSONNEL_CONFLICT_WRITE_REGISTRATION,
                    entityId: actorId,
                    kind: 'patchEntity',
                    payload: {
                        id: actorId,
                        conflicts,
                    },
                }));
            }
        },
        
        clearScheduler: () => set(state => {
            state.rehearsalSchedule = [];
            state.rehearsalScenes = [];
        })
    }
});












